import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { CommandId } from "@cleo/contracts";
import ChatView from "../components/ChatView";
import { threadHasStarted } from "../components/ChatView.logic";
import { LocalView } from "../components/LocalView";
import { useWorldView } from "../neucleos/worldView";
import {
  DraftId,
  markPromotedDraftThreadByRef,
  useComposerDraftSessionStore,
  useComposerContentStore,
} from "../composerDraftStore";
import { waitForDraftHeroTransition } from "../components/chat/draftHeroTransition";
import { buildThreadRouteParams } from "../threadRoutes";
import { useThread, useThreadRefs, useThreadShell, useProjects } from "../state/entities";
import { useAtomCommand } from "../state/use-atom-command";
import { threadEnvironment } from "../state/threads";
import { NO_PROVIDER_MODEL_SELECTION } from "../providerInstances";
import { useNeucleosWorldStore, worldGovernsSessionBirth } from "../neucleos/worldStore";

function DraftChatThreadRouteView() {
  const navigate = useNavigate();
  const { draftId: rawDraftId } = Route.useParams();
  const draftId = DraftId.make(rawDraftId);
  const draftSession = useComposerDraftSessionStore((store) => store.getDraftSession(draftId));
  const projects = useProjects();
  const createThread = useAtomCommand(threadEnvironment.create, { reportFailure: false });
  const [birthError, setBirthError] = useState<string | null>(null);
  const [birthRetry, setBirthRetry] = useState(0);
  const threadRefs = useThreadRefs();
  const inferredThreadRef = draftSession
    ? (threadRefs.find(
        (ref) =>
          ref.environmentId === draftSession.environmentId &&
          ref.threadId === draftSession.threadId,
      ) ?? null)
    : null;
  // A reused draft can carry a promotion to a thread that no longer exists
  // (its conversation or project was deleted before the first message); the
  // draft's own born thread then answers, or the first message would send
  // and the address would never follow it (witnessed 20 September 2026).
  const promotedShell = useThreadShell(draftSession?.promotedTo ?? null);
  const serverThreadRef =
    draftSession?.promotedTo && (promotedShell !== null || inferredThreadRef === null)
      ? draftSession.promotedTo
      : inferredThreadRef;
  const serverThread = useThread(serverThreadRef);
  const serverThreadStarted = threadHasStarted(serverThread);
  const canonicalThreadRef = serverThreadStarted ? serverThreadRef : null;
  const { state: sessionRoom } = useWorldView();
  const bornShell = useThreadShell(inferredThreadRef);

  // A Local session owns an empty selection before its first composer action.
  // The draft's existing thread id and the idempotent device command survive
  // reload, lost acknowledgement and Strict Mode's duplicate mount.
  useEffect(() => {
    if (
      !draftSession ||
      inferredThreadRef ||
      worldGovernsSessionBirth(useNeucleosWorldStore.getState())
    )
      return;
    const project = projects.find(
      (p) => p.id === draftSession.projectId && p.environmentId === draftSession.environmentId,
    );
    if (!project) return;
    const composer = useComposerContentStore.getState().getComposerDraft(draftId);
    const modelSelection =
      (composer?.activeProvider
        ? composer.modelSelectionByProvider[composer.activeProvider]
        : undefined) ??
      project.defaultModelSelection ??
      NO_PROVIDER_MODEL_SELECTION;
    let current = true;
    setBirthError(null);
    void createThread({
      environmentId: draftSession.environmentId,
      input: {
        commandId: CommandId.make(`local-birth-${draftSession.threadId}`),
        threadId: draftSession.threadId,
        projectId: draftSession.projectId,
        title: "New thread",
        modelSelection,
        runtimeMode: draftSession.runtimeMode,
        interactionMode: draftSession.interactionMode,
        branch: draftSession.branch,
        worktreePath: draftSession.worktreePath,
        createdAt: draftSession.createdAt,
      },
    }).then((result) => {
      if (current && result._tag === "Failure")
        setBirthError(
          "The Local conversation could not be opened. Retry to restore its saved selection.",
        );
    });
    return () => {
      current = false;
    };
  }, [draftId, draftSession, inferredThreadRef, projects, createThread, birthRetry]);

  useEffect(() => {
    if (!inferredThreadRef || draftSession?.promotedTo) {
      return;
    }
    markPromotedDraftThreadByRef(inferredThreadRef);
  }, [draftSession?.promotedTo, inferredThreadRef]);

  useEffect(() => {
    if (!canonicalThreadRef) {
      return;
    }

    let cancelled = false;
    void waitForDraftHeroTransition().then(() => {
      if (cancelled) {
        return;
      }
      void navigate({
        to: "/$environmentId/$threadId",
        params: buildThreadRouteParams(canonicalThreadRef),
        replace: true,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [canonicalThreadRef, navigate]);

  useEffect(() => {
    if (draftSession || canonicalThreadRef) {
      return;
    }
    void navigate({ to: "/", replace: true });
  }, [canonicalThreadRef, draftSession, navigate]);

  if (!draftSession) {
    return null;
  }

  const chatView = (
    <ChatView
      draftId={draftId}
      environmentId={draftSession.environmentId}
      threadId={draftSession.threadId}
      routeKind="draft"
      forceExpandedMobileComposer
    />
  );
  // RM-CAN-14: an unstarted Local conversation wears the same room as a
  // started one. Local births the draft's thread before the first message
  // (above), so once that ungoverned shell stands, this leaf seats the SAME
  // thread in LocalView — the room `/$environmentId/$threadId` mounts — and
  // the first message stays in the conversation it was typed in (the composer
  // store resolves the thread to this draft's content). Until the shell
  // arrives, and anywhere outside Local, the classic card is the fallback.
  const wearsRoom =
    sessionRoom.kind === "local" && bornShell !== null && bornShell.worldKey == null;
  // The _chat layout owns the SidebarInset frame; this leaf supplies only its
  // content (PLAN.md Arc A1).
  return (
    <>
      {birthError ? (
        <div role="alert" className="px-3 py-2 text-sm text-warning">
          {birthError}{" "}
          <button type="button" onClick={() => setBirthRetry((value) => value + 1)}>
            Retry
          </button>
        </div>
      ) : null}
      {wearsRoom ? (
        <LocalView
          environmentId={draftSession.environmentId}
          threadId={draftSession.threadId}
          fallback={chatView}
        />
      ) : (
        chatView
      )}
    </>
  );
}

export const Route = createFileRoute("/_chat/draft/$draftId")({
  component: DraftChatThreadRouteView,
});
